import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  REPORT_MAX_PHOTOS,
  REPORT_PHOTO_CAPTION_MAX_LENGTH,
  REPORT_PHOTO_CAPTION_MAX_NEWLINES,
  REPORT_PHOTO_MAX_BYTES,
  type ReportPhotoInput,
} from "@mytour/shared";
import {useEffect, useRef, useState} from "react";
import type {
  Control,
  FieldArray,
  FieldArrayPath,
  FieldErrors,
  FieldPath,
  FieldPathByValue,
  FieldValues,
  UseFormGetValues,
  UseFormTrigger,
} from "react-hook-form";
import {Controller, useFieldArray} from "react-hook-form";
import {Button, FormFeedback, FormGroup, Input, Label} from "reactstrap";

export type PhotoProcessingState = {isProcessing: boolean; hasError: boolean};

type FailedAttempt = {id: number; file: File; message: string};
type ReportPhotosForm = {photos: ReportPhotoInput[]};

export type ReportPhotosLabels = {
  sectionLabel: string;
  photoAlt: (index: number) => string;
  counter: (count: number, maximum: number) => string;
  limitMessage: string;
  cameraLabel?: string;
  galleryLabel?: string;
  captionLabel?: (index: number) => string;
};

type Props<T extends FieldValues> = {
  name: FieldArrayPath<T> & FieldPathByValue<T, ReportPhotoInput[]>;
  control: Control<T>;
  error?: FieldErrors<ReportPhotosForm>["photos"];
  getValues: UseFormGetValues<T>;
  trigger: UseFormTrigger<T>;
  disabled: boolean;
  onStateChange: (state: PhotoProcessingState) => void;
  compressPhotoFile: (
    file: File,
    options: {maxLongSide: number; targetBytes: number; maxBytes: number}
  ) => Promise<string>;
  labels: ReportPhotosLabels;
};

export function ReportPhotosField<T extends FieldValues>({
  name,
  control,
  error,
  getValues,
  trigger,
  disabled,
  onStateChange,
  compressPhotoFile,
  labels,
}: Props<T>) {
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const photoArray = useFieldArray({control, name});
  const {fields} = photoArray;
  const {append, remove} = photoArray;
  const [isProcessing, setIsProcessing] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState<FailedAttempt[]>([]);
  const busyRef = useRef(false);
  const failedAttemptsRef = useRef<FailedAttempt[]>([]);
  const nextAttemptId = useRef(0);
  const generationRef = useRef(0);

  useEffect(() => {
    return () => {
      generationRef.current += 1;
    };
  }, []);

  const unavailable = disabled || isProcessing;
  const atLimit = fields.length >= REPORT_MAX_PHOTOS;

  return (
    <section aria-label={labels.sectionLabel} style={{marginBottom: "16px"}}>
      <div style={{display: "flex", justifyContent: "space-between", marginBottom: "8px"}}>
        <h5 style={{marginBottom: 0}}>Photos</h5>
        <span>{labels.counter(fields.length, REPORT_MAX_PHOTOS)}</span>
      </div>
      <div style={{display: "flex", flexDirection: "column", gap: "8px"}}>
        {fields.map((photo, index) => (
          <div
            key={photo.id}
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              border: "1px solid #dee2e6",
              borderRadius: "6px",
              padding: "8px",
            }}
          >
            <img
              src={getValues(`${name}.${index}.dataUrl` as FieldPath<T>) as string}
              alt={labels.photoAlt(index + 1)}
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "5px",
                objectFit: "contain",
                flex: "none",
              }}
            />
            <FormGroup floating style={{flex: 1, marginBottom: 0}}>
              <Controller
                name={`${name}.${index}.caption` as FieldPath<T>}
                control={control}
                render={({field}) => (
                  <Input
                    {...field}
                    type="textarea"
                    id={`field-${name}-caption-${index}`}
                    placeholder="Description facultative"
                    value={field.value ?? ""}
                    maxLength={REPORT_PHOTO_CAPTION_MAX_LENGTH}
                    disabled={unavailable}
                    invalid={!!error?.[index]?.caption}
                    onChange={(event) => {
                      field.onChange(event);
                      if (error?.[index]?.caption) {
                        void trigger(`${name}.${index}.caption` as FieldPath<T>);
                      }
                    }}
                  />
                )}
              />
              <Label for={`field-${name}-caption-${index}`}>
                {labels.captionLabel?.(index + 1) ??
                  `Description photo ${index + 1} (facultative)`}
              </Label>
              {error?.[index]?.caption && (
                <FormFeedback>{error[index]?.caption?.message}</FormFeedback>
              )}
            </FormGroup>
            <button
              type="button"
              aria-label={`Supprimer la photo ${index + 1}`}
              disabled={unavailable}
              onClick={() => remove(index)}
              style={{
                border: "none",
                background: "transparent",
                color: "var(--bs-danger)",
                fontSize: "18px",
                padding: "0 4px",
                flex: "none",
              }}
            >
              <FontAwesomeIcon icon="trash" />
            </button>
          </div>
        ))}
      </div>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        aria-label={labels.cameraLabel ?? "Prendre une photo"}
        hidden
        disabled={unavailable || atLimit}
        onChange={handleSelection}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        aria-label={labels.galleryLabel ?? "Choisir des photos"}
        hidden
        disabled={unavailable || atLimit}
        onChange={handleSelection}
      />
      <div style={{display: "flex", gap: "8px", marginTop: "10px", flexWrap: "wrap"}}>
        <Button
          type="button"
          outline
          color="primary"
          disabled={unavailable || atLimit}
          onClick={() => cameraRef.current?.click()}
        >
          {labels.cameraLabel ?? "Prendre une photo"}
        </Button>
        <Button
          type="button"
          outline
          color="primary"
          disabled={unavailable || atLimit}
          onClick={() => galleryRef.current?.click()}
        >
          {labels.galleryLabel ?? "Choisir des photos"}
        </Button>
      </div>
      <div style={{fontSize: "12px", color: "#6c757d", marginTop: "4px"}}>
        Description facultative : {REPORT_PHOTO_CAPTION_MAX_LENGTH} caractères et{" "}
        {REPORT_PHOTO_CAPTION_MAX_NEWLINES} retours à la ligne maximum.
      </div>
      {atLimit && <div role="status">{labels.limitMessage}</div>}
      {isProcessing && <div role="status">Préparation des photos…</div>}
      {failedAttempts.map((attempt) => (
        <div key={attempt.id} role="alert" className="text-danger" style={{marginTop: "8px"}}>
          <div>
            {attempt.file.name} : {attempt.message}
          </div>
          <Button
            type="button"
            size="sm"
            color="link"
            disabled={unavailable || atLimit}
            onClick={() => void handleFiles([attempt.file], attempt.id)}
          >
            Réessayer
          </Button>
          <Button
            type="button"
            size="sm"
            color="link"
            disabled={unavailable}
            onClick={() =>
              handleFailedAttemptsChange(
                failedAttemptsRef.current.filter(({id}) => id !== attempt.id)
              )
            }
          >
            Retirer la tentative
          </Button>
        </div>
      ))}
      {typeof error?.message === "string" && (
        <div role="alert" className="text-danger">
          {error.message}
        </div>
      )}
    </section>
  );

  function handleFailedAttemptsChange(attempts: FailedAttempt[]) {
    failedAttemptsRef.current = attempts;
    setFailedAttempts(attempts);
    onStateChange({isProcessing: busyRef.current, hasError: attempts.length > 0});
  }

  function handleSelection(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    void handleFiles(files);
  }

  async function handleFiles(files: File[], retryId?: number) {
    if (disabled || busyRef.current || files.length === 0) {
      return;
    }
    busyRef.current = true;
    setIsProcessing(true);
    const generation = generationRef.current;
    onStateChange({isProcessing: true, hasError: failedAttemptsRef.current.length > 0});
    if (retryId !== undefined) {
      handleFailedAttemptsChange(failedAttemptsRef.current.filter(({id}) => id !== retryId));
    }
    try {
      for (const file of files) {
        try {
          if ((getValues(name) as ReportPhotoInput[]).length >= REPORT_MAX_PHOTOS) {
            throw new Error(
              `Limite de ${REPORT_MAX_PHOTOS} photos atteinte. Retirez cette tentative ou une photo avant de réessayer.`
            );
          }
          const dataUrl = await compressPhotoFile(file, {
            maxLongSide: 1600,
            targetBytes: 200 * 1024,
            maxBytes: REPORT_PHOTO_MAX_BYTES,
          });
          if (generation !== generationRef.current) {
            return;
          }
          if ((getValues(name) as ReportPhotoInput[]).length >= REPORT_MAX_PHOTOS) {
            throw new Error(`Limite de ${REPORT_MAX_PHOTOS} photos atteinte.`);
          }
          append({dataUrl, caption: ""} as FieldArray<T, FieldArrayPath<T>>);
        } catch (error) {
          if (generation !== generationRef.current) {
            return;
          }
          handleFailedAttemptsChange([
            ...failedAttemptsRef.current,
            {
              id: nextAttemptId.current++,
              file,
              message:
                error instanceof Error
                  ? error.message
                  : "Photo illisible. Choisissez un JPEG ou reprenez la photo.",
            },
          ]);
        }
      }
    } finally {
      if (generation === generationRef.current) {
        busyRef.current = false;
        setIsProcessing(false);
        onStateChange({isProcessing: false, hasError: failedAttemptsRef.current.length > 0});
      }
    }
  }
}
